import React, { useState, useEffect, useRef, useMemo } from 'react';
import { CPT_UNIT_TIMES } from '@/features/setup-wizard/constants/cptCodes';
import { calculateAddonFromDuration, resolveAppointmentTypeVariant } from '@/utils/appointmentFormUtils';
import type { CptCodeEntry, SelectedAddon, AppointmentTypeVariant } from '@/hooks/useCptAddonSelection';
import { findCptForDuration } from '@/hooks/useCptAddonSelection';

const INPUT_CLASS =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500';

interface EncounterFormProps {
  selectedPatientForNote: { name: string };
  newEncounter: {
    date: string;
    time: string;
    location_id: string;
    appointment_type_id: string;
    appointment_type_variant_id?: string;
    cpt_code: string;
    duration_minutes: number;
    units?: number;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setNewEncounter: (val: any) => void;
  locations: Array<{ id: string; name: string }>;
  cptCodes: Array<{ code: string; description?: string }>;
  appointmentTypes?: Array<{ id: string; name: string; category?: string; duration_minutes?: number; allowed_durations?: number[] | null; cpt_codes?: CptCodeEntry[]; variants?: AppointmentTypeVariant[] }>;
  selectedAddons?: SelectedAddon[];
  onAddonToggle?: (addon: CptCodeEntry) => void;
  onAppointmentTypeChange?: (typeId: string) => void;
  customAddons: Array<{ code: string; description: string; units: number }>;
  setCustomAddons: React.Dispatch<React.SetStateAction<Array<{ code: string; description: string; units: number }>>>;
  customCptMode?: boolean;
  onToggleCustomCpt?: (mode: boolean) => void;
  primaryCptCodes?: CptCodeEntry[];
  addonCptCodes?: CptCodeEntry[];
  totalAddonFee?: number;
  allowedDurations?: number[] | null;
  formError: string | null;
  formLoading: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onSkip: () => void;
  onCancel: () => void;
}

function EncounterForm({
  selectedPatientForNote,
  newEncounter,
  setNewEncounter,
  locations,
  cptCodes,
  appointmentTypes = [],
  selectedAddons = [],
  onAddonToggle,
  onAppointmentTypeChange,
  customAddons,
  setCustomAddons,
  customCptMode = false,
  onToggleCustomCpt,
  primaryCptCodes = [],
  addonCptCodes = [],
  totalAddonFee = 0,
  allowedDurations = null,
  formError,
  formLoading,
  onSubmit,
  onSkip,
  onCancel,
}: EncounterFormProps) {
  const [cptExplainer, setCptExplainer] = useState('');
  const [showCustomDuration, setShowCustomDuration] = useState(false);

  const [showCustomAddonPicker, setShowCustomAddonPicker] = useState(false);
  const prevDurationRef = useRef<number | null>(null);

  const sortedDurations = useMemo(
    () => (allowedDurations ? [...allowedDurations].sort((a, b) => a - b) : []),
    [allowedDurations],
  );

  // Auto-update CPT code and variant when duration changes
  useEffect(() => {
    const currentDuration = newEncounter.duration_minutes;
    const prev = prevDurationRef.current;
    prevDurationRef.current = currentDuration || null;
    if (!currentDuration || prev === null || currentDuration === prev) return;

    // Prefer variant for auto-fill, fall back to CPT mapping range match
    const selectedType = appointmentTypes.find(t => t.id === newEncounter.appointment_type_id);
    const variants = selectedType?.variants ?? [];
    const activeVariants = variants.filter((v: AppointmentTypeVariant) => v.is_active);
    const typeCategory = (selectedType?.category || '').toLowerCase();
    const prefer60 = ['consultation', 'group_therapy'].includes(typeCategory);
    const variant = resolveAppointmentTypeVariant(activeVariants, currentDuration, prefer60);

    if (variant) {
      const updates: Record<string, unknown> = {
        appointment_type_variant_id: variant.id,
        units: variant.default_units || 1,
      };
      if (variant.default_cpt_code) {
        updates.cpt_code = variant.default_cpt_code;
        // Auto-calculate add-on CPT from duration
        const addon = calculateAddonFromDuration(variant.default_cpt_code, currentDuration);
        if (addon) {
          const addonCpt = cptCodes.find(c => c.code === addon.code);
          setCustomAddons([{ code: addon.code, description: addonCpt?.description ?? addon.code, units: addon.units }]);
        } else {
          setCustomAddons([]);
        }
      }
      setNewEncounter({ ...newEncounter, ...updates });
      const typeName = selectedType?.name || 'this type';
      if (variant.default_cpt_code) {
        setCptExplainer(`CPT updated to ${variant.default_cpt_code} — ${typeName} ${currentDuration} min variant`);
      }
    } else {
      const match = findCptForDuration(currentDuration, primaryCptCodes);
      if (match) {
        setNewEncounter({ ...newEncounter, cpt_code: match.cptCode.code, appointment_type_variant_id: '' });
        const typeName = selectedType?.name || 'this type';
        setCptExplainer(`CPT updated to ${match.cptCode.code} — ${typeName} ${match.rangeLabel} rule`);
      } else if (primaryCptCodes.some(c => c.duration_min_minutes != null || c.duration_max_minutes != null)) {
        setNewEncounter({ ...newEncounter, appointment_type_variant_id: '' });
        setCptExplainer('No default CPT for this duration — please select manually');
      }
    }
  }, [newEncounter.duration_minutes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear explainer after delay
  useEffect(() => {
    if (!cptExplainer) return;
    const timer = setTimeout(() => setCptExplainer(''), 8000);
    return () => clearTimeout(timer);
  }, [cptExplainer]);

  // Sync custom duration mode when type changes
  useEffect(() => {
    const numVal = newEncounter.duration_minutes;
    if (sortedDurations.length > 0 && numVal > 0 && !sortedDurations.includes(numVal)) {
      setShowCustomDuration(true);
    } else {
      setShowCustomDuration(false);
    }
  }, [sortedDurations, newEncounter.duration_minutes]);

  const handleTypeChange = (typeId: string) => {
    const type = appointmentTypes.find((t) => t.id === typeId);
    const updates: Record<string, unknown> = {
      ...newEncounter,
      appointment_type_id: typeId,
      appointment_type_variant_id: '',
    };
    if (type) {
      updates.duration_minutes = type.duration_minutes;
      const duration = type.duration_minutes || newEncounter.duration_minutes;

      // Variant-first: find canonical variant (single, unique default, or 60-min for consult/group)
      const variants: AppointmentTypeVariant[] = type.variants ?? [];
      const activeVariants = variants.filter((v: AppointmentTypeVariant) => v.is_active);
      const typeCategory = (type.category || '').toLowerCase();
      const prefer60 = ['consultation', 'group_therapy'].includes(typeCategory);
      const defaultVariant = resolveAppointmentTypeVariant(activeVariants, duration, prefer60);

      if (defaultVariant) {
        updates.appointment_type_variant_id = defaultVariant.id;
        updates.duration_minutes = defaultVariant.duration_minutes;
        updates.units = defaultVariant.default_units || 1;
        if (defaultVariant.default_cpt_code) {
          updates.cpt_code = defaultVariant.default_cpt_code;
        }
      } else {
        const primaryCodes = (type.cpt_codes ?? []).filter((c) => !c.is_addon);
        const match = findCptForDuration(duration, primaryCodes);
        if (match) {
          updates.cpt_code = match.cptCode.code;
        } else {
          const defaultCpt = primaryCodes.find((c) => c.is_default);
          updates.cpt_code = defaultCpt ? defaultCpt.code : '';
        }
      }
    }
    setNewEncounter(updates);
    onAppointmentTypeChange?.(typeId);
    setCptExplainer('');
    setShowCustomDuration(false);
    prevDurationRef.current = null;
  };

  return (
    <div className="px-3 mb-2">
      <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border border-gray-300 dark:border-gray-600">
        <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
          Create Encounter for {selectedPatientForNote.name}
        </h3>
        {formError && (
          <div className="mb-3 p-2 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 rounded">
            {formError}
          </div>
        )}
        <form onSubmit={onSubmit} className="space-y-2">
          {/* Date / Time */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Date *
              </label>
              <input
                type="date"
                required
                value={newEncounter.date}
                onChange={(e) => setNewEncounter({ ...newEncounter, date: e.target.value })}
                className={INPUT_CLASS}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Time *
              </label>
              <input
                type="time"
                required
                value={newEncounter.time}
                onChange={(e) => setNewEncounter({ ...newEncounter, time: e.target.value })}
                className={INPUT_CLASS}
              />
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Location *
            </label>
            <select
              required
              value={newEncounter.location_id}
              onChange={(e) => setNewEncounter({ ...newEncounter, location_id: e.target.value })}
              className={INPUT_CLASS}
            >
              <option value="">Select location...</option>
              {locations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name}
                </option>
              ))}
            </select>
          </div>

          {/* Appointment Type */}
          {appointmentTypes.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Appointment Type
              </label>
              <select
                value={newEncounter.appointment_type_id || ''}
                onChange={(e) => handleTypeChange(e.target.value)}
                className={INPUT_CLASS}
              >
                <option value="">Select appointment type...</option>
                {appointmentTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name} ({type.duration_minutes} min)
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Variant Selector */}
          {(() => {
            const selectedType = appointmentTypes.find(t => t.id === newEncounter.appointment_type_id);
            const variants = (selectedType?.variants ?? []).filter(v => v.is_active);
            if (variants.length <= 1) return null;
            return (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Variant
                </label>
                <select
                  value={newEncounter.appointment_type_variant_id || ''}
                  onChange={(e) => {
                    const variant = variants.find(v => v.id === e.target.value);
                    if (variant) {
                      setNewEncounter({
                        ...newEncounter,
                        appointment_type_variant_id: variant.id,
                        duration_minutes: variant.duration_minutes,
                        cpt_code: variant.default_cpt_code || newEncounter.cpt_code,
                        units: variant.default_units || 1,
                      });
                      prevDurationRef.current = variant.duration_minutes;
                      // Auto-calculate add-on CPT from duration
                      if (variant.default_cpt_code) {
                        const addon = calculateAddonFromDuration(variant.default_cpt_code, variant.duration_minutes);
                        if (addon) {
                          const addonCpt = cptCodes.find(c => c.code === addon.code);
                          setCustomAddons([{ code: addon.code, description: addonCpt?.description ?? addon.code, units: addon.units }]);
                        } else {
                          setCustomAddons([]);
                        }
                      }
                    }
                  }}
                  className={INPUT_CLASS}
                >
                  <option value="">Select variant...</option>
                  {variants.map((v) => {
                    const variantLabel = v.label
                      || (v.variant_key ? v.variant_key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '');
                    const cptInfo = v.default_cpt_code
                      ? ` (${v.default_cpt_code}${v.default_cpt_description ? ` — ${v.default_cpt_description}` : ''})`
                      : '';
                    return (
                      <option key={v.id} value={v.id}>
                        {variantLabel ? `${variantLabel} · ` : ''}{v.duration_minutes} min{cptInfo}
                      </option>
                    );
                  })}
                </select>
              </div>
            );
          })()}

          {/* CPT Code */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                CPT Code *
              </label>
              {primaryCptCodes.length > 0 && (
                <button
                  type="button"
                  onClick={() => onToggleCustomCpt?.(!customCptMode)}
                  className="text-xs text-blue-500 hover:text-blue-700 dark:text-blue-400"
                >
                  {customCptMode ? 'Select from list' : 'Enter custom code'}
                </button>
              )}
            </div>
            {customCptMode || primaryCptCodes.length === 0 ? (
              <>
                <input
                  type="text"
                  required
                  value={newEncounter.cpt_code}
                  onChange={(e) => setNewEncounter({ ...newEncounter, cpt_code: e.target.value })}
                  placeholder="Type a CPT code..."
                  list="encounter-cpt-datalist"
                  className={INPUT_CLASS}
                />
                <datalist id="encounter-cpt-datalist">
                  {cptCodes.map((cpt) => (
                    <option key={cpt.code} value={cpt.code}>
                      {cpt.code} — {cpt.description}
                    </option>
                  ))}
                </datalist>
              </>
            ) : (
              <select
                required
                value={newEncounter.cpt_code}
                onChange={(e) => setNewEncounter({ ...newEncounter, cpt_code: e.target.value })}
                className={INPUT_CLASS}
              >
                <option value="">Select CPT code...</option>
                <optgroup label="Configured for this type">
                  {primaryCptCodes.map((cpt) => (
                    <option key={cpt.code} value={cpt.code}>
                      {cpt.code} — {cpt.description}
                    </option>
                  ))}
                </optgroup>
                {cptCodes.length > 0 && (
                  <optgroup label="All CPT codes">
                    {cptCodes
                      .filter((c) => !primaryCptCodes.some((p) => p.code === c.code))
                      .map((cpt) => (
                        <option key={cpt.code} value={cpt.code}>
                          {cpt.code} — {cpt.description}
                        </option>
                      ))}
                  </optgroup>
                )}
              </select>
            )}
          </div>

          {/* Add-on Codes */}
          {(addonCptCodes.length > 0 || customAddons.length > 0 || newEncounter.appointment_type_id) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Add-on Codes
              </label>
              <div className="space-y-1 bg-gray-50 dark:bg-gray-750 rounded-lg p-2 border border-gray-200 dark:border-gray-600">
                {addonCptCodes.map((addon) => {
                  const isSelected = selectedAddons.some((a) => a.code === addon.code);
                  const addonDuration =
                    addon.additional_duration_minutes ?? CPT_UNIT_TIMES[addon.code] ?? 0;
                  const addonFee = Number(addon.private_pay_fee) || 0;
                  return (
                    <label
                      key={addon.code}
                      className="flex items-start gap-2 p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => onAddonToggle?.(addon)}
                        className="mt-0.5 rounded border-gray-300 dark:border-gray-500"
                      />
                      <div className="text-sm leading-snug">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {addon.code}
                        </span>
                        {addon.description && (
                          <span className="text-gray-600 dark:text-gray-400 ml-1">
                            — {addon.description}
                          </span>
                        )}
                        {addonDuration > 0 && (
                          <span className="text-blue-600 dark:text-blue-400 ml-1 text-xs">
                            (+{addonDuration} min)
                          </span>
                        )}
                        {addonFee > 0 && (
                          <span className="text-green-600 dark:text-green-400 ml-1 text-xs">
                            (+${addonFee})
                          </span>
                        )}
                      </div>
                    </label>
                  );
                })}
                {/* Custom add-on rows */}
                {customAddons.map((custom) => (
                  <div key={custom.code} className="flex items-center gap-2 py-0.5">
                    <span className="font-medium text-sm text-gray-900 dark:text-white">{custom.code}</span>
                    <span className="text-sm text-gray-600 dark:text-gray-400 flex-1 truncate">{custom.description}</span>
                    <input
                      type="number"
                      min="1"
                      max="99"
                      value={custom.units}
                      onChange={(e) => {
                        const val = Math.max(1, parseInt(e.target.value, 10) || 1);
                        setCustomAddons(customAddons.map(a => a.code === custom.code ? { ...a, units: val } : a));
                      }}
                      className="w-14 px-1 py-0.5 text-center border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <button
                      type="button"
                      onClick={() => setCustomAddons(customAddons.filter(a => a.code !== custom.code))}
                      className="text-red-500 hover:text-red-700 text-xs px-1"
                      aria-label="Remove"
                    >✕</button>
                  </div>
                ))}
                {/* "+ Add CPT code" button */}
                {showCustomAddonPicker ? (
                  <div className="flex items-center gap-2 mt-1">
                    <select
                      onChange={(e) => {
                        const cpt = cptCodes.find(c => c.code === e.target.value);
                        if (cpt) {
                          setCustomAddons([...customAddons, { code: cpt.code, description: cpt.description || '', units: 1 }]);
                          setShowCustomAddonPicker(false);
                        }
                      }}
                      value=""
                      className={INPUT_CLASS}
                    >
                      <option value="" disabled>Select a CPT code...</option>
                      {cptCodes
                        .filter(c => !addonCptCodes.some(a => a.code === c.code) && !customAddons.some(a => a.code === c.code))
                        .map(cpt => (
                          <option key={cpt.code} value={cpt.code}>{cpt.code} — {cpt.description}</option>
                        ))}
                    </select>
                    <button type="button" onClick={() => setShowCustomAddonPicker(false)} className="text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowCustomAddonPicker(true)}
                    className="flex items-center gap-1 mt-1 text-sm text-blue-500 hover:text-blue-700 dark:text-blue-400"
                  >
                    <span>+</span> Add CPT code
                  </button>
                )}
              </div>
              {totalAddonFee > 0 && (
                <p className="mt-1 text-sm text-green-700 dark:text-green-300">
                  Add-on fees total: ${totalAddonFee.toFixed(2)}
                </p>
              )}
            </div>
          )}

          {/* Units */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Units
            </label>
            <input
              type="number"
              min="1"
              max="99"
              value={newEncounter.units || 1}
              onChange={(e) =>
                setNewEncounter({
                  ...newEncounter,
                  units: Math.max(1, parseInt(e.target.value, 10) || 1),
                })
              }
              className={`${INPUT_CLASS} w-20`}
              style={{ width: 80, textAlign: 'center' }}
            />
          </div>

          {/* Duration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Duration (minutes)
            </label>
            {sortedDurations.length > 0 && !showCustomDuration ? (
              <div className="flex items-center gap-2">
                <select
                  value={newEncounter.duration_minutes || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '__custom__') {
                      setShowCustomDuration(true);
                    } else {
                      setNewEncounter({
                        ...newEncounter,
                        duration_minutes: Number(val) || 0,
                      });
                    }
                  }}
                  className={INPUT_CLASS}
                >
                  <option value="">Select duration...</option>
                  {sortedDurations.map((d) => (
                    <option key={d} value={d}>
                      {d > 120 ? `${Math.floor(d / 60)}h${d % 60 ? ` ${d % 60}m` : ''}` : `${d} min`}
                    </option>
                  ))}
                  <option value="__custom__">Custom...</option>
                </select>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="15"
                  step="15"
                  value={newEncounter.duration_minutes}
                  onChange={(e) =>
                    setNewEncounter({
                      ...newEncounter,
                      duration_minutes: Number(e.target.value) || 0,
                    })
                  }
                  className={INPUT_CLASS}
                />
                {sortedDurations.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowCustomDuration(false)}
                    className="text-xs text-blue-500 hover:text-blue-700 dark:text-blue-400 whitespace-nowrap"
                  >
                    Use preset
                  </button>
                )}
              </div>
            )}
            {cptExplainer && (
              <p className="mt-1 text-xs text-blue-600 dark:text-blue-400">
                {cptExplainer}
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 mt-2">
            <button
              type="submit"
              disabled={formLoading}
              className="flex-1 px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white rounded-lg transition-colors"
            >
              {formLoading ? 'Creating...' : 'Create Encounter & Write Note'}
            </button>
            <button
              type="button"
              onClick={onSkip}
              className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors"
            >
              Skip Encounter
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 bg-gray-300 hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-900 dark:text-white rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EncounterForm;
